<template>
  <div class="is-pc pc-qrcode">
    <header class="pc-header">
      <div class="header-left">
        <h1>三高体检 · 内勤管理</h1>
        <span class="page-title">业务员注册二维码</span>
      </div>
      <button class="back-btn" @click="goBack">返回列表</button>
    </header>

    <main class="pc-body">
      <div class="qrcode-card">
        <p class="qrcode-hint">业务员使用微信扫码即可进入注册页面</p>

        <div class="org-block">
          <span class="org-label">所属机构</span>
          <strong class="org-name">{{ orgName || "机构信息加载中" }}</strong>
          <span v-if="orgId" class="org-id">机构编号 {{ orgId }}</span>
        </div>

        <div class="qrcode-image-wrap">
          <img
            ref="qrImage"
            :src="qrImageUrl"
            alt="业务员注册二维码"
            class="qrcode-image"
            @load="qrLoadFailed = false"
            @error="qrLoadFailed = true"
          />
          <p v-if="qrLoadFailed" class="qrcode-error">二维码加载失败，请刷新页面重试</p>
        </div>

        <div class="link-block">
          <p class="link-label">链接地址（已带机构ID）</p>
          <p class="link-content">{{ link || "生成中..." }}</p>
        </div>

        <div class="action-row">
          <button class="ghost-btn" @click="copyLink">复制链接</button>
          <button class="primary-btn" :disabled="buildingPoster || !link" @click="downloadQr">{{ buildingPoster ? "生成中…" : "下载注册分享图" }}</button>
        </div>

        <p class="capture-tip">
          下载的图片包含机构名称、扫码用途和二维码，可直接发给业务员。注册后自动关联当前机构。
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import { pcToast } from "./pc-ui";
import { buildQrImageUrl } from "@/config/sanGao";
import { getSanGaoStaffInfo } from "@/api/sanGaoStaff";
import { isTaikangStaffMode } from "@/utils/sanGaoStaffMode";
import { buildSalesmanRegistrationPoster } from "../salesman-registration-poster";

const STORAGE_KEYS = {
  info: "san_gao_staff_info",
};

export default {
  name: "SanGaoStaffPcSalesmanQrcode",
  data() {
    return {
      orgId: "",
      orgName: "",
      link: "",
      qrImageUrl: "",
      qrLoadFailed: false,
      buildingPoster: false,
    };
  },
  created() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      this.orgId = this.$route.query.orgId || "";
      const cached = localStorage.getItem(STORAGE_KEYS.info);
      if (cached) {
        try {
          const info = JSON.parse(cached);
          if (!this.orgId) this.orgId = info.orgId ? String(info.orgId) : "";
          if (String(info.orgId) === String(this.orgId)) this.orgName = info.orgName || "";
        } catch (e) {
          // 忽略损坏的缓存，继续请求机构信息
        }
      }
      try {
        const res = await getSanGaoStaffInfo();
        if (res && res.code === 200 && res.data && res.data.orgId) {
          if (!this.orgId) this.orgId = String(res.data.orgId);
          if (String(res.data.orgId) === String(this.orgId)) this.orgName = res.data.orgName || this.orgName;
        }
      } catch (e) {
        // 请求失败时保留匹配当前机构的缓存；登录失效由拦截器处理
      }
      if (!this.orgId) {
        pcToast("未获取到机构信息，请重新登录");
        return;
      }
      this.buildLink();
    },
    async buildLink() {
      // 分享链接携带机构及当前入口标记，页面二维码和分享图共用此链接。
      const origin = window.location.origin;
      const modeQuery = isTaikangStaffMode(this.$route.query) ? "&taikang=1" : "";
      this.link = `${origin}/sanGao/salesman/login?orgId=${encodeURIComponent(this.orgId)}&type=register${modeQuery}`;
      this.qrImageUrl = await buildQrImageUrl(this.link, 320);
    },
    async copyLink() {
      if (!this.link) {
        pcToast("链接未生成");
        return;
      }
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.link);
        } else {
          const input = document.createElement("input");
          input.value = this.link;
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        pcToast("链接已复制");
      } catch (e) {
        pcToast("复制失败，请手动复制链接");
      }
    },
    async downloadQr() {
      if (this.buildingPoster) return;
      if (!this.link) {
        pcToast("链接未生成");
        return;
      }
      if (!this.orgName) {
        pcToast("机构名称未获取到，请刷新页面重试");
        return;
      }
      this.buildingPoster = true;
      try {
        const imageUrl = await buildSalesmanRegistrationPoster(this.link, this.orgName, this.orgId);
        const a = document.createElement("a");
        a.href = imageUrl;
        a.download = `三高修复_业务员注册分享图_${this.orgId}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (e) {
        pcToast("分享图生成失败，请重试");
      } finally {
        this.buildingPoster = false;
      }
    },
    goBack() {
      this.$router.replace("/sanGao/staff/pc/index");
    },
  },
};
</script>

<style lang="less" scoped>
.is-pc.pc-qrcode {
  min-height: 100vh;
  background: #eef5fa;
}

.is-pc .pc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 28px;
  background: linear-gradient(100deg, #0a6bb8, #168de0);
  color: #fff;
}

.is-pc .header-left {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.is-pc .pc-header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.is-pc .page-title {
  font-size: 14px;
  opacity: 0.9;
}

.is-pc .back-btn {
  height: 30px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 15px;
  font-size: 13px;
  color: #fff;
  background: transparent;
  cursor: pointer;
}

.is-pc .back-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}

.is-pc .pc-body {
  display: flex;
  justify-content: center;
  padding: 40px 24px;
}

.is-pc .qrcode-card {
  width: 460px;
  padding: 30px 34px 26px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(12, 91, 147, 0.1);
  text-align: center;
}

.is-pc .qrcode-hint {
  margin: 0 0 18px;
  font-size: 13px;
  color: #5b7488;
}

.is-pc .org-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  margin: 0 0 20px;
  padding: 14px;
  border-radius: 10px;
  background: #f1f8fd;
}

.is-pc .org-label, .is-pc .org-id { font-size: 12px; color: #6b8395; }
.is-pc .org-name { font-size: 18px; line-height: 1.5; color: #163b5c; }

.is-pc .qrcode-image-wrap {
  position: relative;
  display: inline-block;
  margin: 0 auto 18px;
  padding: 12px;
  border: 1px solid #eef4f9;
  border-radius: 12px;
  background: #fafdff;
}

.is-pc .qrcode-image {
  display: block;
  width: 260px;
  height: 260px;
}

.is-pc .qrcode-error {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  font-size: 12px;
  color: #b14545;
  background: #fde0e0;
  border-radius: 12px;
}

.is-pc .link-block {
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 10px;
  text-align: left;
  background: #f4fafd;
}

.is-pc .link-label {
  margin: 0 0 4px;
  font-size: 11px;
  color: #7c8d9c;
}

.is-pc .link-content {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  word-break: break-all;
  color: #163b5c;
}

.is-pc .action-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}

.is-pc .ghost-btn {
  height: 38px;
  border: 1px solid #168de0;
  border-radius: 8px;
  font-size: 13px;
  color: #168de0;
  background: #fff;
  cursor: pointer;
}

.is-pc .ghost-btn:hover {
  background: rgba(22, 141, 224, 0.08);
}

.is-pc .primary-btn {
  height: 38px;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(100deg, #19a2e5, #146bd2);
  cursor: pointer;
}

.is-pc .primary-btn:hover {
  opacity: 0.92;
}

.is-pc .primary-btn:disabled { cursor: not-allowed; opacity: 0.65; }

.is-pc .capture-tip {
  margin: 0;
  font-size: 11px;
  line-height: 1.6;
  color: #7c8d9c;
}
</style>
